/**
 * Site Features API client: the adapter for GET/PATCH
 * /admin-api/site/{siteId}/features.
 *
 * Mirrors the branding client's conventions: a Keycloak bearer token, one retry
 * on 401, and the `message` string every non-2xx response carries.
 */

import { track } from '../../analytics';
import type { ApiErrorAttributes } from '../../analytics';
import type { SiteFeaturesResponse } from '../models/apiModels';
import type { SiteFeaturesPatchRequest } from '../utils/featuresPatch';

export interface SiteFeaturesApiClientConfig {
  readonly apiHost: string;
  readonly getAccessToken: () => Promise<string | null>;
}

/**
 * Everything other than success.
 *
 * `not_found` is "no such site". It is expected for sites that have no Toptracer
 * Go configuration yet, so it is neither tracked nor reported as an error.
 */
export type SiteFeaturesFailure =
  | { readonly status: 'not_found' }
  | { readonly status: 'unauthorized' }
  | { readonly status: 'forbidden' }
  | { readonly status: 'invalid'; readonly message: string }
  | { readonly status: 'error'; readonly message: string };

export type SiteFeaturesResult =
  | { readonly status: 'ok'; readonly data: SiteFeaturesResponse }
  | SiteFeaturesFailure;

const featuresPath = (apiHost: string, siteId: string): string =>
  `${apiHost}/admin-api/site/${encodeURIComponent(siteId)}/features`;

function trackApiError(errorType: string): void {
  const attributes: ApiErrorAttributes = { ErrorType: errorType };
  track('Pocket Admin API Error', attributes);
}

/** Read the documented `message`, falling back when the body is not the documented JSON. */
async function readMessage(response: Response): Promise<string> {
  try {
    const body = await response.json();
    const message = (body as { message?: unknown })?.message;
    return typeof message === 'string' ? message : `API error: ${response.status}`;
  } catch {
    return `API error: ${response.status}`;
  }
}

async function toResult(response: Response): Promise<SiteFeaturesResult> {
  if (response.ok) {
    return { status: 'ok', data: await response.json() };
  }

  if (response.status === 404) return { status: 'not_found' };

  trackApiError(`SiteFeatures_HTTP_${response.status}`);

  // A 401 has already been retried by the time it gets here. A 403 means the
  // caller lacks `tgo-admin`; re-authenticating cannot grant a role.
  if (response.status === 401) return { status: 'unauthorized' };
  if (response.status === 403) return { status: 'forbidden' };

  const message = await readMessage(response);

  // 400 is a malformed body, an unknown flag or a non-boolean value.
  if (response.status === 400) return { status: 'invalid', message };

  return { status: 'error', message };
}

/**
 * Issue a request, retrying once with a fresh token on a 401.
 *
 * `getAccessToken` only renews a token that is already expired, so a token
 * expiring in flight yields a 401 that a second attempt resolves.
 */
async function requestWithRetry(
  doRequest: (token: string) => Promise<Response>,
  config: SiteFeaturesApiClientConfig
): Promise<Response> {
  const token = await config.getAccessToken();
  if (!token) throw new Error('No access token available');

  const response = await doRequest(token);
  if (response.status !== 401) return response;

  const freshToken = await config.getAccessToken();
  if (!freshToken) throw new Error('No access token available');

  return doRequest(freshToken);
}

const authHeaders = (token: string): HeadersInit => ({
  Authorization: `Bearer ${token}`,
});

const jsonHeaders = (token: string): HeadersInit => ({
  ...authHeaders(token),
  'Content-Type': 'application/json',
});

/** Read the site's current flags. */
export async function fetchSiteFeatures(
  siteId: string,
  config: SiteFeaturesApiClientConfig
): Promise<SiteFeaturesResult> {
  const response = await requestWithRetry(
    (token) => fetch(featuresPath(config.apiHost, siteId), { headers: authHeaders(token) }),
    config
  );
  return toResult(response);
}

/**
 * Update only the flags named in `patch`.
 *
 * Takes effect for players on their next site config request. Concurrent writers
 * resolve as last writer wins; the returned state is what was stored.
 */
export async function patchSiteFeatures(
  siteId: string,
  patch: SiteFeaturesPatchRequest,
  config: SiteFeaturesApiClientConfig
): Promise<SiteFeaturesResult> {
  const response = await requestWithRetry(
    (token) =>
      fetch(featuresPath(config.apiHost, siteId), {
        method: 'PATCH',
        headers: jsonHeaders(token),
        body: JSON.stringify(patch),
      }),
    config
  );
  return toResult(response);
}
